import { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Eye, EyeOff } from 'lucide-react';
import {
  useAdminTenantStatus,
  useAdminTenantUptime,
  useAdminTenantIncidents,
  useAdminTenantMaintenances,
} from '../hooks/useTenantStatus.ts';
import { fetchAdminTenantProbes } from '../api/tenant-status.api.ts';
import { HeroBanner } from '@/pages/public/components/HeroBanner.tsx';
import { StatusBars } from '@/pages/public/components/StatusBars.tsx';
import { ActiveIncidents } from '@/pages/public/components/ActiveIncidents.tsx';
import { ScheduledMaint } from '@/pages/public/components/ScheduledMaint.tsx';
import { IncidentHistory } from '@/pages/public/components/IncidentHistory.tsx';

export function TenantStatusPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Página interna abre na visão "real" (barras por amostra bruta); admin pode alternar p/ contratual.
  const [rawMode, setRawMode] = useState(true);
  // No modo real, opcionalmente descontar as janelas de manutenção do cálculo dos cards.
  const [excludeMaint, setExcludeMaint] = useState(false);
  // "Olho": emula a visão externa — força contratual e esconde os controles internos.
  const [previewPublic, setPreviewPublic] = useState(false);

  // No preview a página se comporta como a pública (contratual, sem desconto extra de manutenção).
  const effRaw = previewPublic ? false : rawMode;
  const effExclude = previewPublic ? false : excludeMaint;

  const { data: statusData, isLoading: loadingStatus } = useAdminTenantStatus(id!);
  const { data: uptimeData } = useAdminTenantUptime(id!, effRaw, effExclude);
  const { data: incidentsData } = useAdminTenantIncidents(id!);
  const { data: maintenancesData } = useAdminTenantMaintenances(id!);

  const fetchProbes = useCallback(
    (from: string, to: string) => fetchAdminTenantProbes(id!, from, to),
    [id],
  );

  if (loadingStatus || !statusData) {
    return (
      <div className="flex h-96 items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  const now = Date.now();
  const thirtyDaysAgo = now - 30 * 86_400_000;
  const incidentes30d = incidentsData
    ? [...incidentsData.abertos, ...incidentsData.historico].filter(
        (i) => new Date(i.iniciadoEm).getTime() >= thirtyDaysAgo,
      ).length
    : 0;

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Voltar + olho de pré-visualização */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ArrowLeft size={14} />
            Tenants
          </button>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {statusData.tenant.nome}
          </span>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm text-gray-500">Monitoramento</span>
        </div>

        <button
          onClick={() => setPreviewPublic((v) => !v)}
          aria-pressed={previewPublic}
          title={previewPublic ? 'Sair da pré-visualização pública' : 'Pré-visualizar como a página pública'}
          className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
            previewPublic
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
          }`}
        >
          {previewPublic ? <EyeOff size={14} /> : <Eye size={14} />}
          {previewPublic ? 'Sair da pré-visualização' : 'Ver como público'}
        </button>
      </div>

      {previewPublic && (
        <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
          <Eye size={14} className="flex-shrink-0" />
          Pré-visualização da página pública — é exatamente o que o cliente vê (contratual, sem os controles internos).
        </div>
      )}

      {/* Conteúdo — mesma estrutura da página pública */}
      <div className="mx-auto max-w-3xl space-y-6">
        <HeroBanner
          tenantNome={statusData.tenant.nome}
          tenantSlug={statusData.tenant.slug}
          status={statusData.status}
          // Preview público e Real usam o mês do getUptime; Contratual (admin) usa o valor do SLA.
          uptimeMes={previewPublic || rawMode ? uptimeData?.uptimeMesAtual ?? null : statusData.uptimeMes}
          uptime90dias={uptimeData?.uptime90dias ?? null}
          uptime12meses={uptimeData?.uptime12meses ?? null}
          incidentes30d={incidentes30d}
          latenciaMs={statusData.latenciaMs}
        />

        {uptimeData && (
          <StatusBars
            historico={uptimeData.historico}
            historicoHoras={uptimeData.historicoHoras ?? []}
            fetchProbesFn={fetchProbes}
            // No preview público, some com os controles internos (a pública não tem toggle).
            modeToggle={previewPublic ? undefined : { raw: rawMode, onChange: setRawMode }}
            maintenanceToggle={previewPublic ? undefined : { exclude: excludeMaint, onChange: setExcludeMaint }}
          />
        )}

        {incidentsData && <ActiveIncidents incidents={incidentsData.abertos} />}

        {maintenancesData && <ScheduledMaint maintenances={maintenancesData.agendadas} />}

        {incidentsData && incidentsData.historico.length > 0 && (
          <IncidentHistory incidents={incidentsData.historico} />
        )}
      </div>
    </div>
  );
}
